import LoadingButton from '@mui/lab/LoadingButton';
import { Autocomplete, Box, Icon, Paper, TextField, useMediaQuery } from "@mui/material";
import { DateRange, LocalizationProvider, PickersShortcutsItem, StaticDateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import AutoCompleteEstados from "@/components/AutoCompleteEstados";
import AutoCompleteUsers from "@/components/AutoCompleteUsers";
import AutoCompleteVelocidades from "@/components/AutoCompleteVelocidades";
import MyToastShow from '@/components/Layout/MyToast';
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { IUser } from '@/redux/models';
import { listaDeInformes } from "@/Routes/listaDeInformes";
import CustomFilterParams from "./CustomFilterParams";


const listaInformes = () => {
    const tiposDeInforme = listaDeInformes.map(({ tipos }) => tipos)
    const vtipos: any[] = []
    tiposDeInforme.forEach((tipo) => {
        Object.entries(tipo).forEach(([key, { nombre }]) => {
            if (vtipos.find((item) => item.value === key)) return
            vtipos.push({ atributo: key, contenido: nombre })
        })
    })
    return vtipos

}

const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
        label: 'Hoy',
        getValue: () => {
            const today = dayjs();
            return [today, today];
        },
    },
    {
        label: 'Semana pasada',
        getValue: () => {
            const today = dayjs();
            const prevWeek = today.subtract(7, 'day');
            return [prevWeek.startOf('week'), prevWeek.endOf('week')];
        },
    },
    {
        label: 'Últimos 7 días',
        getValue: () => {
            const today = dayjs();
            return [today.subtract(7, 'day'), today];
        },
    },
    {
        label: 'Último mes',
        getValue: () => {
            const today = dayjs();
            const prevMonth = today.subtract(1, 'month');
            return [prevMonth, today];
        },
    },
    {
        label: 'Últimos dos meses',
        getValue: () => {
            const today = dayjs();
            const prevMonth = today.subtract(2, 'month');
            return [prevMonth, today];
        },
    },

    { label: 'Reiniciar', getValue: () => [null, null] },
];


function FiltroFechas({ onChange }: { onChange: (date: DateRange<Dayjs>) => void }) {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDateRangePicker
                sx={{
                    mr: 3,
                    mb: 3,
                    borderRadius: '15px',
                }}
                onChange={onChange}
                localeText={{
                    toolbarTitle: 'Filtrar por fecha',
                    start: 'Inicio',
                    end: 'Fin',
                }}
                slotProps={{
                    shortcuts: {
                        items: shortcutsItems,
                    },
                    actionBar: { actions: [] },
                }}
                calendars={1}
                disableFuture
            />
        </LocalizationProvider>
    );
}

const NavFiltros = ({ onSubmit }: { onSubmit: (filters: any) => Promise<void> }) => {

    const isMobile = useMediaQuery('(max-width:1200px)');

    const [tipoInforme, setTipoInforme] = useState<{ atributo: string, contenido: string }>()
    const [atributoYContenido, setAtributoYContenido] = useState<any>({})
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState<IUser>();
    const [fechas, setFechas] = useState<DateRange<Dayjs>>([null, null]);
    const [calle, setCalle] = useState<string>('')
    const [alturas, setAlturas] = useState<string>('')
    const [plazo, setPlazo] = useState<string>('')



    const FiltrosValidos = (query: any) => {
        let validos = true

        if (Object.keys(query).length === 0) {
            MyToastShow({ message: 'Debe ingresar al menos un filtro', type: 'error' })
            return false
        }

        return validos
    }


    const handleSearch = () => {

        const query = {
            ...(fechas[0] && { fechaDesde: dayjs(fechas[0]).format('DD/MM/YYYY') }),
            ...(fechas[1] && { fechaHasta: dayjs(fechas[1]).format('DD/MM/YYYY') }),
            ...((calle || (calle && alturas)) && { domicilio: [{ calle, alturas }] }),
            ...(user && { userId: user.id }),
            ...(Object.keys(atributoYContenido).length > 0 && { criteria: atributoYContenido }),
            ...(tipoInforme !== null && { criteria: { ...atributoYContenido, tipo: tipoInforme?.atributo } }),
        }
        

        if (FiltrosValidos(query)) {
            setLoading(true)
            onSubmit(query).finally(() => setLoading(false))
        }
    }



    return (<>
        <Paper>
            <Box aria-label="inner-content" sx={{ display: 'flex', flexDirection: 'column', p: '1rem' }}>

                <Box display='flex' sx={{
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: 'space-around'
                }}>

                    <Box sx={{ width: 'fit-content' }}>
                        <FiltroFechas onChange={(date) => setFechas(date)} />
                    </Box>
                    <Box display='flex' flexDirection='column' alignSelf='start'>
                    <Box sx={{ display: 'flex', flexDirection: ['column', 'row'], mb: 3 }}>
                        <AutoCompleteUsers
                            sx={{ width: '15rem', mr: [0, 3], mb: [3, 0], pt: 4 }}
                            label="Filtrar por usuario"
                            onChange={setUser}
                            value={user}
                        />                  

                        <Box sx={{ width: '8rem', mr: [0, 3], pt: 4, mb: [3, 0] }}>
                            <AutoCompleteVelocidades
                                onChange={(value) => setAtributoYContenido((prev: any) => ({ ...prev, ['velocidad']: value }))}
                                value={plazo}
                                label="Plazo"
                            />
                        </Box>                  

                        <Box sx={{ width: '8rem', pt: 4 }}>
                            <AutoCompleteEstados
                                onChange={(value) => setAtributoYContenido((prev: any) => ({ ...prev, ['estadoGeneral']: value }))}
                                value={''}
                                label="Estado"
                            />
                        </Box>
                    </Box>

                        <Box>
                            <CustomFilterParams
                                onChange={(k, v) => {
                                    k = k.toLowerCase()

                                    if (v === '') {
                                        setAtributoYContenido((prev: any) => {
                                            delete prev[k]
                                            return prev
                                        })
                                        return
                                    }
                                    setAtributoYContenido((prev: any) => ({ ...prev, [k]: v }))
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box display='flex' aria-label="select-users-calle-alturas-search" sx={{
                    alignItems: 'center',
                    marginTop: isMobile ? '1rem' : '0',
                    flexDirection: isMobile ? 'column' : 'row',
                }}>
                    <Autocomplete
                        sx={{ width: '20rem' }}
                        options={listaInformes()}
                        defaultValue={[]}
                        getOptionLabel={(option) => option.contenido}
                        onChange={(e, value) => {
                            setTipoInforme(value)
                        }}
                        value={tipoInforme || null}
                        isOptionEqualToValue={(option, value) => {
                            return option.atributo === value.atributo
                        }}
                        filterSelectedOptions
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Tipos de Informe"
                                placeholder="Informes"
                            />
                        )}
                    />
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: 3,
                        mt: isMobile ? 2 : 0

                    }}>
                        <TextField label="Calle" sx={{ mx: 3 }}
                            onChange={(e) => setCalle(e.target.value)}
                            value={calle}

                        />
                        <TextField label="Alturas"
                            onChange={(e) => setAlturas(e.target.value)}
                            value={alturas}
                        />

                        <LoadingButton
                            sx={{ height: '100%', ml: 3 }}
                            onClick={handleSearch}
                            endIcon={<Icon>search</Icon>}
                            loading={loading}
                            loadingPosition="end"
                            variant="contained"
                        >
                            <span>Buscar</span>
                        </LoadingButton>

                    </Box>

                </Box>

            </Box>
        </Paper>


    </>);
}

export default NavFiltros;